import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import LabeledValue from '../../../../components/common/LabeledValue'
import Modal from '../../../../components/common/Modal/Modal'
import { TimeOffRequestDetails,  isVacationBid, } from '../../../../store/slices/Requestor/timeOffDetailsSlice'
import { normalizeStatus, TABLE_COLUMNS, getDisplayedOtherHoursTotal, isVacBidDayInOtherRequestsChecked } from './constants'
import { RootState } from '../../../../store/store'
import RightPanel from './RightPanel'
import CurrentRequestSection from './CurrentRequestSection'
import HistorySection from './HistorySection'
import WithdrawnView from './WithdrawnView'
import useWithdrawRequest from '../../../../hooks/useWithdrawRequest'
import { formatDateToMMDDYY, isRequestDateBeforeToday } from '../../../../utils/date'

interface FieldConfig {
  label: string
  value: React.ReactNode
}

interface HoldFieldsArgs {
  data: TimeOffRequestDetails
  isOnHold: boolean
  isReleased: boolean
  isHoldState: boolean
}

interface SubmittedFieldsArgs {
  data: TimeOffRequestDetails
  isHoldState: boolean
  vacationBid: boolean
  submittedByValue: string | undefined
}

const getHeading = (isOnHold: boolean, vacationBid: boolean, normalizedStatus: string): string => {
  if (normalizedStatus === 'rtsubmitted_onhold') {
    return 'Request for Time Off Retraction On Hold'
  }

  if (normalizedStatus === 'prtsubmitted_onhold') {
    return 'Time Off Request Partial Retraction On Hold'
  }

  if (isOnHold) {
    return vacationBid
      ? 'Submitted Vacation Bid Request On Hold'
      : 'Submitted Time Off Request On Hold'
  }

  return vacationBid
    ? 'Submitted Vacation Bid Request'
    : 'Submitted Time Off Request'
}

const getSubmittedByValue = (data: TimeOffRequestDetails): string | undefined => {
  if (!data.submittedBy) {
    return data.submittedBy
  }

  // Only label the submitter a proxy when their nuid actually differs from the
  // request owner's — not merely because manualReqRcvdDate is set.
  const submitterNuid = data.submittedBy.split('(')[1]?.replace(')', '').trim().toLowerCase()
  const ownerNuid = data.nuid?.trim().toLowerCase()

  return submitterNuid && ownerNuid && submitterNuid !== ownerNuid
    ? `Requestor Proxy ${data.submittedBy}`
    : data.submittedBy
}

const getHoldFields = ({
  data,
  isOnHold,
  isReleased,
  isHoldState,
}: HoldFieldsArgs): FieldConfig[] => {
  const fields: FieldConfig[] = []

  if (isHoldState && data.holdPlacedOn) {
    fields.push({
      label: isReleased ? 'Hold Released On' : 'Hold Placed On',
      value: data.holdPlacedOn,
    })
  }

  if (isHoldState && data.holdPlacedBy) {
    fields.push({
      label: isReleased ? 'Released By' : 'Hold Placed By',
      value: data.holdPlacedBy,
    })
  }

  /**
   * For on-hold requests, show Request Rank if:
   * 1. torRank is populated (regular time-off or vacation bid)
   * 2. It's a vacation bid request (even if torRank is null, display as "NA")
   * This aligns with approver flow behavior where vacation bids always show rank.
   */
  const vacationBidRequest = isVacationBid(data)
  if (isOnHold && (data.requestRank != null || vacationBidRequest)) {
    fields.push({
      label: 'Request Rank',
      value: data.requestRank == null ? 'NA' : String(data.requestRank),
    })
  }

  if (isHoldState) {
    fields.push({
      label: 'Approver Comments',
      value: data.approverComments || '-',
    })
  }

  return fields
}

const getSubmittedFields = ({
  data,
  isHoldState,
  vacationBid,
  submittedByValue,
}: SubmittedFieldsArgs): FieldConfig[] => {
  if (isHoldState) {
    return []
  }

  const fields: FieldConfig[] = []

  if (data.submittedOn) {
    fields.push({
      label: 'Submitted On',
      value: data.submittedOn,
    })
  }

  if (submittedByValue) {
    fields.push({
      label: 'Submitted By',
      value: submittedByValue,
    })
  }

  if (vacationBid || data.requestRank != null) {
    fields.push({
      label: 'Request Rank',
      value: data.requestRank == null ? 'NA' : String(data.requestRank),
    })
  }

  fields.push({
    label: 'Employee Comments',
    value: data.employeeComments || '-',
  })

  if (data.manualReqRcvdDate) {
    fields.push({
      label: 'Manual Request Received Date',
      value: formatDateToMMDDYY(data.manualReqRcvdDate),
    })
  }

  return fields
}

const renderLabeledFields = (fields: FieldConfig[]): JSX.Element[] =>
  fields.map((field) => (
    <div className="mb-4" key={field.label}>
      <LabeledValue label={field.label} value={field.value} />
    </div>
  ))

interface SubmittedOrOnHoldViewProps {
  data: TimeOffRequestDetails
  tableRows: any[]
  highlightedDates: string[]
  handlePrint?: () => void
}

const SubmittedOrOnHoldView: React.FC<SubmittedOrOnHoldViewProps> = ({
  data,
  tableRows,
  highlightedDates,
  handlePrint,
}) => {

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)

  const {
    withdraw,
    loading: withdrawLoading,
    error: withdrawError,
  } = useWithdrawRequest()

  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails?.data
  )

  const ns = normalizeStatus(data.status)
  const isOnHold =
    ns === 'on hold' ||
    ns === 'submitted_onhold' ||
    ns === 'rtsubmitted_onhold' ||
    ns === 'prtsubmitted_onhold'
  const isReleased = ns === 'submitted_released'
  const isHoldState = isOnHold || isReleased
  const vacationBid = isVacationBid(data)
  const heading = getHeading(isOnHold, vacationBid, ns)
  const submittedByValue = getSubmittedByValue(data)
  const holdFields = getHoldFields({
    data,
    isOnHold,
    isReleased,
    isHoldState,
  })
  const originalTimeOffRequestId = data.originalRequestId ?? data.partialRefId
  const holdFieldsWithReferenceId =
    ns === 'prtsubmitted_onhold' && originalTimeOffRequestId
      ? [
          ...holdFields,
          {
            label: 'Original Time Off Request ID',
            value: (
              <Link to={`/requestor/status/${encodeURIComponent(originalTimeOffRequestId)}`}>
                {originalTimeOffRequestId}
              </Link>
            ),
          },
        ]
      : holdFields
  const submittedFields = getSubmittedFields({
    data,
    isHoldState,
    vacationBid,
    submittedByValue,
  })
  const requestorHistory = data.history ?? []
  const shouldRenderHistory = requestorHistory.length > 0
  // Disable Withdraw if the request contains any date before today (e.g. a
  // multi-day request that has already begun). isRequestDateBeforeToday
  // compares plain date strings (no Date-object timezone conversion), so the
  // button disables at end of day in the viewer's own timezone rather than
  // shifting by their UTC offset.
  const isWithdrawDisabled = isRequestDateBeforeToday(data.todate)

  /**
   * Vacation Bid's "Day in Other Requests" column renders as a checkbox
   * (matching legacy), not the numeric/breakdown value used for regular
   * time-off requests — same treatment as DraftView's Current Request table.
   */
  const currentRequestTableColumns = vacationBid
    ? TABLE_COLUMNS.map((col) =>
        col.key === 'hoursInOtherRequests'
          ? { ...col, label: 'Day in Other Requests' }
          : col
      )
    : TABLE_COLUMNS

  // tableRows comes from buildTableRows (constants.ts), which always returns
  // hoursInOtherRequests as a non-empty formatted string (e.g. "0.00") — never
  // use its JS truthiness directly for the checkbox; parse the actual total.
  const vacationBidTableRows = vacationBid
    ? tableRows.map((row) => ({
        ...row,
        hoursInOtherRequests: getDisplayedOtherHoursTotal(String(row.hoursInOtherRequests ?? '')) > 0,
      }))
    : tableRows

  const renderVacationBidOverlapCell = vacationBid
    ? (row: any, col: { key: string }) => {
        if (col.key !== 'hoursInOtherRequests') return row[col.key]
        if (row._totalRow || row.id === 'total') return ''
        return (
          <input
            type="checkbox"
            className="table-cell-checkbox"
            checked={isVacBidDayInOtherRequestsChecked(otherReqData, row.date, row.toReqDtlId ?? null)}
            disabled
            readOnly
            aria-label="Day in other requests"
          />
        )
      }
    : undefined

  if (withdrawn) {
    return (
      <WithdrawnView
        data={{ ...data, status: 'Withdrawn' }}
        tableRows={vacationBidTableRows}
        highlightedDates={highlightedDates}
        handlePrint={handlePrint}
        showSuccessBanner
      />
    )
  }

  return (
    <div>
      <h1 className="h1">{heading}</h1>

      <div className="flex flex-col mobile:gap-4 tablet:gap-6 desktop:gap-8 desktop:flex-row">
        <div className="flex-1">
          {!isHoldState && (
            <div className="mb-6">
              <p className="p3 -medium">
                This time off request was submitted and is pending review by your approver.
              </p>
            </div>
          )}

          {renderLabeledFields(isHoldState ? holdFieldsWithReferenceId : submittedFields)}

          <button type="button"
            className="button -primary -inverted mt-8!"
            disabled={isWithdrawDisabled}
            onClick={() => {
              if (!isWithdrawDisabled) {
                setIsWithdrawOpen(true)
              }
            }}
          >
            Withdraw Request
          </button>
        </div>

        <RightPanel
          data={data}
          variant={isHoldState ? 'full' : 'name-id'}
          handlePrint={handlePrint}
        />
      </div>

      <div className="mt-6!">
        <hr className="-variation-1" />
      </div>

      {shouldRenderHistory && (
        <HistorySection history={requestorHistory} />
      )}

      <CurrentRequestSection
        tableRows={vacationBidTableRows}
        highlightedDates={highlightedDates}
        tableColumns={currentRequestTableColumns}
        renderCell={renderVacationBidOverlapCell}
      />

      <Modal
        isOpen={isWithdrawOpen}
        title="Withdraw Request"
        onClose={() => setIsWithdrawOpen(false)}
        submitBtn="Withdraw Request"
        backBtn="Cancel"
        loading={withdrawLoading}
        onPrimary={async () => {
          const withdrawSuccess = await withdraw(
            data.requestId,
            data.nuid || ''
          )

          if (withdrawSuccess) {
            setIsWithdrawOpen(false)
            setWithdrawn(true)
          }
          return withdrawSuccess
        }}
        onBack={() => setIsWithdrawOpen(false)}
      >
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
          }}
        >
          <img
            className="w-10 h-10 shrink-0"
            src="/assets/images/alertSolid.svg"
            alt="warning"
          />

          <div>
            <p className="p2 -semibold">
              You are requesting to withdraw this time off request. It will be
              removed from the approvers queue.
            </p>

            {withdrawError && (
              <p className="p3 -medium text-red-600 mt-2">{withdrawError}</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default SubmittedOrOnHoldView
